import React from 'react';
import canUsePassiveEvents from './canUsePassiveEvents';

export const DEFAULT_IGNORE_CLASS = 'ignore-onclickoutside';

const hasIgnoreClass = (event, ignoreClass) => {
    let element = event.target;

    while (element) {
        if (element.classList && element.classList.contains(ignoreClass)) {
            return true;
        }

        element = element.parentElement;
    }

    return false;
};

const clickedOnScrollbar = (event) =>
    document.documentElement.clientWidth <= event.clientX ||
    document.documentElement.clientHeight <= event.clientY;

const getEventOpts = (type) =>
    type.includes('touch') && canUsePassiveEvents() ? { passive: true } : false;

const defaultOpts = {
    disabled: false,
    eventTypes: ['mousedown', 'touchstart'],
    excludeScrollbar: false,
    ignoreClass: DEFAULT_IGNORE_CLASS,
};

const useOnClickOutside = (cb, opts = defaultOpts) => {
    const { disabled, eventTypes, excludeScrollbar, ignoreClass } = opts;
    const cbRef = React.useRef(cb);
    const [refsState, setRefsState] = React.useState([]);

    React.useEffect(() => {
        cbRef.current = cb;
    }, [cb]);

    const ref = React.useCallback((element) => {
        setRefsState((prevState) => [...prevState, { current: element }]);
    }, []);

    React.useEffect(() => {
        if (refsState.length === 0) return;
        const listener = (event) => {
            if (hasIgnoreClass(event, ignoreClass)) return;

            const rs = refsState;
            const els = [];

            rs.forEach(({ current }) => {
                if (current) els.push(current);
            });

            if (excludeScrollbar && clickedOnScrollbar(event)) return;
            if (
                els.length === 0 ||
                !els.every((el) => !el.contains(event.target))
            )
                return;

            cbRef.current(event);
        };

        const removeEventListener = () => {
            eventTypes.forEach((type) => {
                document.removeEventListener(
                    type,
                    listener,
                    getEventOpts(type)
                );
            });
        };

        if (disabled) {
            removeEventListener();
            return;
        }

        eventTypes.forEach((type) => {
            document.addEventListener(type, listener, getEventOpts(type));
        });

        return () => {
            removeEventListener();
        };
    }, [
        refsState,
        ignoreClass,
        excludeScrollbar,
        disabled,
        JSON.stringify(eventTypes),
    ]);

    return ref;
};

export default useOnClickOutside;
